import { useEffect, useMemo, useRef, useState } from 'react';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import { scrollModePlugin } from '@react-pdf-viewer/scroll-mode';
import { toolbarPlugin } from '@react-pdf-viewer/toolbar';
import { themePlugin } from '@react-pdf-viewer/theme';
import { zoomPlugin } from '@react-pdf-viewer/zoom';

import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';

import { useThemeContext } from '../../../../../contexts/ThemeContext';
import { resolveColor } from '../../../theme/utils/resolveColor';
import { isMobile } from '../../../../../utils/utils';
import { PoweredByQITech } from '../PoweredByQITech';
import * as S from './styles';

interface DocumentViewerProps {
    url: string | null;
    onBack: () => void;
    title?: string;
    pollForUrl?: () => Promise<string | null | undefined>;
    pollIntervalMs?: number;
    pollTimeoutMs?: number;
}

const renderMinimalToolbar = (Toolbar: any) => (
    <Toolbar>
        {(slots: any) => {
            const {
                ZoomOut,
                Zoom,
                ZoomIn,
                GoToPreviousPage,
                GoToNextPage,
                Print,
                Download,
            } = slots;

            return (
                <div
                    style={{
                        alignItems: 'center',
                        display: 'flex',
                        width: '100%',
                        backgroundColor: '#242424',
                        padding: '4px',
                        ['--rpv-core__minimal-button-color' as any]: '#ffffff',
                        ['--rpv-toolbar__label-color' as any]: '#ffffff',
                        ['--rpv-core__minimal-button-hover-background-color' as any]:
                            'rgba(255, 255, 255, 0.1)',
                    }}
                >
                    <style>{`
                        .rpv-core__minimal-button,
                        .rpv-core__icon {
                            color: #ffffff !important;
                        }
                        .rpv-core__minimal-button:disabled {
                            color: #555555 !important;
                            cursor: not-allowed;
                        }
                    `}</style>
                    <div style={{ display: 'flex', width: '100%' }}>
                        <div style={{ display: 'flex', flex: '1', justifyContent: 'flex-start' }}>
                            <div style={{ padding: '0 2px' }}><ZoomOut /></div>
                            <div style={{ padding: '0 2px' }}><Zoom /></div>
                            <div style={{ padding: '0 2px' }}><ZoomIn /></div>
                        </div>

                        <div style={{ display: 'flex', flex: '1', justifyContent: 'center' }}>
                            <div style={{ padding: '0 2px' }}><GoToPreviousPage /></div>
                            <div style={{ padding: '0 2px' }}><GoToNextPage /></div>
                        </div>

                        <div style={{ display: 'flex', flex: '1', justifyContent: 'flex-end' }}>
                            <div style={{ padding: '0 2px' }}><Download /></div>
                            <div style={{ padding: '0 2px' }}><Print /></div>
                        </div>
                    </div>
                </div>
            );
        }}
    </Toolbar>
);

export const PrivatePayrollAuctionDocumentViewer = ({
    url,
    onBack,
    title = 'Documento',
    pollForUrl,
    pollIntervalMs = 15000,
    pollTimeoutMs = 120000,
}: DocumentViewerProps) => {
    const { clientThemeSettings } = useThemeContext();
    const primary = resolveColor('primary', clientThemeSettings);

    const scrollModePluginInstance = scrollModePlugin();
    const toolbarPluginInstance = toolbarPlugin();
    const { Toolbar } = toolbarPluginInstance;
    const themePluginInstance = themePlugin();
    const zoomPluginInstance = zoomPlugin({ enableShortcuts: true });

    const onBackRef = useRef(onBack);
    onBackRef.current = onBack;

    const [resolvedUrl, setResolvedUrl] = useState<string | null>(url);
    const [pollStatus, setPollStatus] = useState<'idle' | 'polling' | 'timeout'>(
        url ? 'idle' : (pollForUrl ? 'polling' : 'idle'),
    );
    const [pollAttempt, setPollAttempt] = useState(0);

    const pollForUrlRef = useRef(pollForUrl);
    pollForUrlRef.current = pollForUrl;

    useEffect(() => {
        if (url) {
            setResolvedUrl(url);
            setPollStatus('idle');
        }
    }, [url]);

    useEffect(() => {
        if (resolvedUrl || !pollForUrlRef.current || pollStatus !== 'polling') return;
        let cancelled = false;
        let timeoutId: ReturnType<typeof setTimeout> | null = null;
        const startedAt = Date.now();
        setPollStatus('polling');

        const tick = async () => {
            if (cancelled) return;
            try {
                const next = await pollForUrlRef.current?.();
                if (cancelled) return;
                if (next) {
                    setResolvedUrl(next);
                    setPollStatus('idle');
                    return;
                }
            } catch {}
            if (cancelled) return;
            if (Date.now() - startedAt >= pollTimeoutMs) {
                setPollStatus('timeout');
                return;
            }
            timeoutId = setTimeout(tick, pollIntervalMs);
        };

        timeoutId = setTimeout(tick, pollIntervalMs);

        return () => {
            cancelled = true;
            if (timeoutId) clearTimeout(timeoutId);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resolvedUrl, pollIntervalMs, pollTimeoutMs, pollStatus, pollAttempt]);

    const retryPolling = () => {
        setPollStatus('polling');
        setPollAttempt((n) => n + 1);
    };

    useEffect(() => {
        const alreadyMarked = (window.history.state as any)?.__documentViewerBack;
        if (!alreadyMarked) {
            window.history.pushState({ __documentViewerBack: true }, '');
        }

        const handlePopState = (event: PopStateEvent) => {
            if ((event.state as any)?.__documentViewerBack) return;
            onBackRef.current();
        };

        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, []);

    const pdfWorkerUrl = useMemo(() => {
        try {
            // eslint-disable-next-line global-require, @typescript-eslint/no-var-requires
            const pdfjsVersion = require('pdfjs-dist/package.json')?.version;
            return `https://unpkg.com/pdfjs-dist@${pdfjsVersion}/build/pdf.worker.min.js`;
        } catch {
            return 'https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js';
        }
    }, []);

    const renderFallbackIframe = () => {
        if (!resolvedUrl) return null;
        const iframeSrc = isMobile()
            ? `https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(resolvedUrl)}`
            : resolvedUrl;
        return <S.DocumentFrame src={iframeSrc} title={title} />;
    };

    return (
        <S.PageWrapper>
            <S.LogoSection>
                {clientThemeSettings?.logo_url && (
                    <S.LogoImage src={clientThemeSettings.logo_url} alt="logo" />
                )}
            </S.LogoSection>

            <S.ViewerContainer>
                {resolvedUrl ? (
                    <>
                        {renderMinimalToolbar(Toolbar)}
                        <div style={{ flex: 1, overflow: 'hidden', height: 'calc(100% - 40px)' }}>
                            <Worker workerUrl={pdfWorkerUrl}>
                                <Viewer
                                    theme="dark"
                                    fileUrl={resolvedUrl}
                                    defaultScale={isMobile() ? 0.6 : 1.0}
                                    plugins={[
                                        scrollModePluginInstance,
                                        toolbarPluginInstance,
                                        themePluginInstance,
                                        zoomPluginInstance,
                                    ]}
                                    renderError={() => renderFallbackIframe()}
                                />
                            </Worker>
                        </div>
                    </>
                ) : (
                    <S.FallbackBlock>
                        {pollStatus === 'timeout' ? (
                            <>
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: 48,
                                        height: 48,
                                        borderRadius: '50%',
                                        backgroundColor: `${primary}1a`,
                                        color: primary,
                                        fontSize: 28,
                                        fontWeight: 700,
                                        lineHeight: 1,
                                    }}
                                    aria-hidden
                                >
                                    !
                                </div>
                                <div style={{ fontWeight: 600, fontSize: 16, color: '#1a202c' }}>
                                    O documento ainda não está disponível
                                </div>
                                <div style={{ maxWidth: 320 }}>
                                    Estamos finalizando a preparação do seu documento. Aguarde alguns
                                    instantes e tente novamente. Se o problema persistir, volte mais tarde.
                                </div>
                                <button
                                    type="button"
                                    onClick={retryPolling}
                                    style={{
                                        marginTop: 4,
                                        padding: '10px 18px',
                                        borderRadius: 8,
                                        border: `1px solid ${primary}`,
                                        backgroundColor: primary,
                                        color: '#ffffff',
                                        fontSize: 14,
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                    }}
                                >
                                    Tentar novamente
                                </button>
                            </>
                        ) : (
                            <>
                                <div
                                    style={{
                                        width: 40,
                                        height: 40,
                                        border: `3px solid ${primary}33`,
                                        borderTopColor: primary,
                                        borderRadius: '50%',
                                        animation: 'rpvDocSpin 1s linear infinite',
                                    }}
                                />
                                <style>{`@keyframes rpvDocSpin{to{transform:rotate(360deg);}}`}</style>
                                <div style={{ fontWeight: 600 }}>
                                    Preparando seu documento...
                                </div>
                                <div>
                                    Isso pode levar alguns instantes.
                                </div>
                            </>
                        )}
                    </S.FallbackBlock>
                )}
            </S.ViewerContainer>

            <S.Footer>
                <S.OutlineButton $color={primary} onClick={onBack}>
                    Voltar
                </S.OutlineButton>
                <PoweredByQITech />
            </S.Footer>
        </S.PageWrapper>
    );
};
